import React, { lazy, Suspense, useMemo } from 'react';
import { Redirect, useLocation } from 'react-router-dom';
import { resolveClientAdminSession } from '../core/auth/resolveClientAdminSession';
import { useTokenManager } from '../contexts/TokenManagerContext';

const AdminDashboardShell = lazy(() => import('./AdminDashboardShell'));
const ArtistRouter = lazy(() => import('./ArtistRouter'));

const LoadingSpinner = () => (
  <div
    style={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      height: '100vh',
      backgroundColor: '#02172a',
      color: '#fff',
    }}
  >
    <div>Loading...</div>
  </div>
);

/**
 * `/dashboard` is overloaded in this app:
 * - admins expect the admin dashboard (production-like)
 * - artists/streamers expect their portal dashboard
 *
 * Route selection uses the JWT (authoritative role) plus localStorage flags.
 * Streamers/artists use DB role `user` / `artist`; they must never be sent to
 * the admin shell based only on stale `isAdmin` / `admin` localStorage from dev.
 */
const RoleDashboardRouter: React.FC = () => {
  const { getToken } = useTokenManager();
  const location = useLocation();
  const routingToken = getToken?.() ?? null;

  const isAdmin = useMemo(
    () => resolveClientAdminSession(routingToken),
    [routingToken]
  );

  const hasLegacyArtistId = useMemo(() => {
    try {
      return !!new URLSearchParams(location.search).get('artistId');
    } catch {
      return false;
    }
  }, [location.search]);

  if (!isAdmin && hasLegacyArtistId) {
    return (
      <Redirect
        to={{
          pathname: '/artist/dashboard',
          search: location.search,
        }}
      />
    );
  }

  return (
    <Suspense fallback={<LoadingSpinner />}>
      {isAdmin ? <AdminDashboardShell /> : <ArtistRouter />}
    </Suspense>
  );
};

export default RoleDashboardRouter;
